import React, { useCallback, useRef, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { useBranch } from '../../context/BranchContext';
import { useFilterParams } from '../../hooks';
import QcResultsListTab, { SOURCE_KIND_LABELS } from './QcResultsListTab';
import QcResultsByEmployeeTab from './QcResultsByEmployeeTab';
import QcResultsBySessionTab from './QcResultsBySessionTab';
import QcReviewerQualityTab from './QcReviewerQualityTab';
import QcAuditsTab from './QcAuditsTab';
import QcResultDetailModal from './QcResultDetailModal';
import './QcResultsPage.css';

const VALID_TABS = ['by-session', 'list', 'by-employee', 'reviewer-quality', 'audits'];

// #120936 — trang này TRƯỚC ĐÂY đã đọc `tab`/`branch_id`/`employee_id`/`session_id` từ query
// nhưng CHƯA BAO GIỜ ghi ngược, nên bấm đổi tab/drill-through xong copy link ra là mất trạng thái.
// Nay dùng chung `useFilterParams`: đọc lúc mount + ghi ngược bằng `replace`.
// `branch_id` cố ý KHÔNG khai ở đây — BranchContext đã quản chung cho mọi trang.
//
// #151415 — thêm `source_kind` + `source_id`: cặp định danh 1 kết quả QC (đúng tên tham số API
// `/qc-results/:sourceKind/:id`, khuôn deep-link `urlParams.js`). Khi có ĐỦ cặp này trong URL thì
// mở THẲNG modal "Chi tiết kết quả QC" của đúng kết quả đó — phục vụ link trong tin Telegram
// "Giải trình mới — Cần duyệt" của khoản phạt QC không đạt. `source_kind` ở đây là tham số
// DEEP-LINK của TRANG, KHÔNG phải ô lọc "Nguồn QC" của tab danh sách (ô đó vẫn là state riêng).
const FILTER_SPEC = {
  tab: { default: 'by-session', valid: (v) => VALID_TABS.includes(v) },
  employee_id: { default: '' },
  session_id: { default: '' },
  source_kind: { default: '', valid: (v) => Object.keys(SOURCE_KIND_LABELS).includes(v) },
  source_id: { default: '' },
};

// ON-42: trang tổng hợp/tracking kết quả QC xuyên 4 nguồn (ShiftTask/GameCheck/Loyalty/TablePrep)
export default function QcResultsPage() {
  const { branches, selectedBranch, selectBranch } = useBranch();

  const [filters, setFilters, , { ignored, dismissIgnored }] = useFilterParams(FILTER_SPEC);
  const activeTab = filters.tab;
  // #175146 — người chấm đang được xem hậu kiểm (drill từ tab "Chất lượng người chấm").
  const [auditEmployeeId, setAuditEmployeeId] = useState('');

  // Đọc giá trị mới nhất trong callback mà không phải bám vào chúng ở mảng phụ thuộc.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const selectedBranchIdRef = useRef('');
  selectedBranchIdRef.current = selectedBranch?.id || '';

  // Giữ nguyên hình dạng `sharedFilters` mà 3 tab con đang nhận. `branch_id` nay lấy thẳng từ
  // BranchContext (chính nó đã đọc `?branch_id=` lúc mount) thay vì giữ một bản sao riêng của trang.
  const sharedFilters = {
    branch_id: selectedBranch?.id || '',
    employee_id: filters.employee_id,
    session_id: filters.session_id,
  };

  // Ô chọn chi nhánh trong 2 tab con giờ đổi chi nhánh TOÀN CỤC — nhờ vậy nó tự ghi ngược
  // `?branch_id=` qua BranchContext và ăn khớp với ô chi nhánh trên thanh đầu trang.
  const handleBranchIdChange = useCallback(
    (branchId) => selectBranch(branches.find((b) => b.id === branchId) || null),
    [branches, selectBranch],
  );

  // Bộ điều hợp: 3 tab con vẫn gọi theo hình dạng cũ `(prev) => ({...prev, x})`. Ở đây tách ra —
  // `employee_id`/`session_id` đi vào URL của trang, còn `branch_id` chuyển cho BranchContext.
  const setSharedFilters = useCallback(
    (updater) => {
      const current = {
        branch_id: selectedBranchIdRef.current,
        employee_id: filtersRef.current.employee_id,
        session_id: filtersRef.current.session_id,
      };
      const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater };

      if ((next.branch_id ?? '') !== current.branch_id) {
        handleBranchIdChange(next.branch_id || '');
      }
      setFilters({
        employee_id: next.employee_id ?? '',
        session_id: next.session_id ?? '',
      });
    },
    [setFilters, handleBranchIdChange],
  );

  const setActiveTab = useCallback((tab) => setFilters({ tab }), [setFilters]);

  const handleDrillToEmployee = (employeeId) => {
    setFilters({ employee_id: employeeId, tab: 'list' });
  };

  const handleDrillToSession = (sessionId) => {
    setFilters({ session_id: sessionId, tab: 'list' });
  };

  // #175146 — drill từ "Chất lượng người chấm" sang tab Hậu kiểm, lọc sẵn theo người chấm đó.
  const handleViewAudits = (employeeId) => {
    setAuditEmployeeId(employeeId || '');
    setFilters({ tab: 'audits' });
  };

  // #175146 — mở đúng chi tiết kết quả QC (dùng lại cơ chế deep-link #151415).
  const handleOpenResult = (sourceKind, sourceId) => {
    setFilters({ source_kind: sourceKind, source_id: sourceId });
  };

  // #151415 — chỉ mở modal khi có ĐỦ cặp định danh (thiếu 1 trong 2 thì không biết mở cái nào).
  const detailTarget =
    filters.source_kind && filters.source_id
      ? { sourceKind: filters.source_kind, id: filters.source_id }
      : null;

  return (
    <PageLayout>
      <div className="mc-page qc-results">
        <div className="mc-page-header my-2">
          <div className="mc-page-header-title">
            <h1>Kết quả QC</h1>
            <p className="qc-results-subtitle">Theo dõi chất lượng kiểm tra theo từng phiên, nhân viên và người chấm.</p>
          </div>
        </div>

        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />

        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          <Tab eventKey="by-session" title="Theo phiên">
            <QcResultsBySessionTab
              selectedBranch={selectedBranch}
              branchId={sharedFilters.branch_id}
              onSelectSession={handleDrillToSession}
            />
          </Tab>
          <Tab eventKey="list" title="Tất cả kết quả">
            <QcResultsListTab
              selectedBranch={selectedBranch}
              sharedFilters={sharedFilters}
              onSharedFiltersChange={setSharedFilters}
            />
          </Tab>
          <Tab eventKey="by-employee" title="Theo nhân viên">
            <QcResultsByEmployeeTab
              selectedBranch={selectedBranch}
              branchId={sharedFilters.branch_id}
              sessionId={sharedFilters.session_id}
              onClearSession={() => setFilters({ session_id: '' })}
              onSelectEmployee={handleDrillToEmployee}
            />
          </Tab>
          <Tab eventKey="reviewer-quality" title="Chất lượng người chấm">
            <QcReviewerQualityTab branchId={sharedFilters.branch_id} onViewAudits={handleViewAudits} />
          </Tab>
          <Tab eventKey="audits" title="Hậu kiểm">
            <QcAuditsTab
              branchId={sharedFilters.branch_id}
              employeeId={auditEmployeeId}
              onOpenResult={handleOpenResult}
              onClearEmployee={() => setAuditEmployeeId('')}
            />
          </Tab>
        </Tabs>
      </div>

      {detailTarget && (
        <QcResultDetailModal
          sourceKind={detailTarget.sourceKind}
          id={detailTarget.id}
          onHide={() => setFilters({ source_kind: '', source_id: '' })}
        />
      )}
    </PageLayout>
  );
}
